import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Users, Search, Plus, Star, Tag, Phone, Mail, Calendar, Ruler, MessageSquare, Clock, ChevronRight, X, Trash2, Edit, Save, FileText, Scissors, Package, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  active: "bg-green-500/20 text-green-400",
  vip: "bg-yellow-500/20 text-yellow-400",
  inactive: "bg-gray-500/20 text-gray-400"
};

const AdminCRMPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Add/Edit Client Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    status: "active",
    notes: ""
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchClients();
    }
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/crm/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (client) => {
    setSelectedClient(client);
    setLoadingDetail(true);
    setClientDetail(null);
    
    try {
      const response = await axios.get(`${API}/crm/clients/${client.id || client.email}`);
      setClientDetail(response.data);
    } catch (error) {
      console.error("Failed to fetch client detail:", error);
      setClientDetail({
        client: client,
        appointments: [],
        measurements: [],
        sent_emails: []
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClient) return;
    
    setAddingNote(true);
    try {
      await axios.post(`${API}/crm/clients/${selectedClient.id || selectedClient.email}/notes`, {
        note: newNote
      });
      toast.success("Note added");
      setNewNote("");
      fetchClientDetail(selectedClient);
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedClient?.id) {
      toast.error("Cannot update status for this client");
      return;
    }
    
    try {
      await axios.put(`${API}/crm/clients/${selectedClient.id}`, {
        status: newStatus
      });
      toast.success("Status updated");
      fetchClients();
      fetchClientDetail({ ...selectedClient, status: newStatus });
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  // Open Add Client Modal
  const openAddModal = () => {
    setClientForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      status: "active",
      notes: ""
    });
    setEditMode(false);
    setShowAddModal(true);
  };

  // Open Edit Client Modal
  const openEditModal = (client) => {
    setClientForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      status: client.status || "active",
      notes: ""
    });
    setEditMode(true);
    setShowAddModal(true);
  };

  // Save Client (Create or Update)
  const handleSaveClient = async () => {
    if (!clientForm.name || !clientForm.email) {
      toast.error("Name and email are required");
      return;
    }

    setSavingClient(true);
    try {
      if (editMode && selectedClient) {
        // Update existing client
        await axios.put(`${API}/crm/clients/${selectedClient.id || selectedClient.email}`, clientForm);
        toast.success("Client updated successfully");
        fetchClientDetail({ ...selectedClient, ...clientForm });
      } else {
        // Create new client
        await axios.post(`${API}/crm/clients`, clientForm);
        toast.success("Client added successfully");
      }
      setShowAddModal(false);
      fetchClients();
    } catch (error) {
      console.error("Failed to save client:", error);
      toast.error(error.response?.data?.detail || "Failed to save client");
    } finally {
      setSavingClient(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone?.includes(searchQuery);
    
    const matchesStatus = statusFilter === "all" || client.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                    Client CRM
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {clients.length} clients
                  </p>
                </div>
              </div>
              <Button onClick={openAddModal} className="bg-primary hover:bg-primary/90">
                <Plus size={18} className="mr-2" />
                Add Client
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Client List */}
            <div className="lg:w-1/3">
              {/* Search & Filter */}
              <div className="mb-4 space-y-3">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-background border-white/10"
                  />
                </div>
                <div className="flex gap-2">
                  {["all", "active", "vip", "inactive"].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1 text-xs rounded transition-colors ${
                        statusFilter === status
                          ? "bg-primary text-primary-foreground"
                          : "bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Client List */}
              <div className="bg-card border border-white/10 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Loading clients...</p>
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No clients found</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={openAddModal}>
                      <Plus size={14} className="mr-1" /> Add First Client
                    </Button>
                  </div>
                ) : (
                  filteredClients.map((client, index) => (
                    <motion.div
                      key={client.email}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => fetchClientDetail(client)}
                      className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                        selectedClient?.email === client.email ? "bg-primary/10 border-l-2 border-l-primary" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium truncate">{client.name || "Unknown"}</h4>
                            {client.status === "vip" && (
                              <Star size={14} className="text-yellow-400 fill-yellow-400" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {client.appointment_count || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Ruler size={12} />
                              {client.measurement_count || 0}
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={18} className="text-muted-foreground" />
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Client Detail */}
            <div className="lg:w-2/3">
              {selectedClient ? (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-card border border-white/10"
                >
                  {loadingDetail ? (
                    <div className="p-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">Loading client details...</p>
                    </div>
                  ) : clientDetail ? (
                    <>
                      {/* Client Header */}
                      <div className="p-6 border-b border-white/10">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h2 className="font-serif text-2xl">{clientDetail.client?.name || selectedClient.name}</h2>
                              <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[clientDetail.client?.status || "active"]}`}>
                                {(clientDetail.client?.status || "active").toUpperCase()}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {clientDetail.client?.email || selectedClient.email}
                              </span>
                              {(clientDetail.client?.phone || selectedClient.phone) && (
                                <span className="flex items-center gap-1">
                                  <Phone size={14} />
                                  {clientDetail.client?.phone || selectedClient.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(clientDetail.client || selectedClient)}
                              className="border-white/20"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateStatus("active")}
                              className={`border-white/20 ${clientDetail.client?.status === "active" ? "bg-green-500/20" : ""}`}
                            >
                              Active
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateStatus("vip")}
                              className={`border-white/20 ${clientDetail.client?.status === "vip" ? "bg-yellow-500/20" : ""}`}
                            >
                              <Star size={14} className="mr-1" />
                              VIP
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigate(`/admin/ai-assistant`);
                                // Could pass client email via state
                              }}
                              className="border-white/20"
                            >
                              <MessageSquare size={14} className="mr-1" />
                              Message
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-6 border-b border-white/10">
                        <div className="p-3 text-center border-r border-white/10">
                          <p className="text-xl font-serif text-primary">{clientDetail.appointments?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Appts</p>
                        </div>
                        <div className="p-3 text-center border-r border-white/10">
                          <p className="text-xl font-serif text-primary">{clientDetail.measurements?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Measures</p>
                        </div>
                        <div className="p-3 text-center border-r border-white/10">
                          <p className="text-xl font-serif text-blue-400">{clientDetail.invoices?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Invoices</p>
                        </div>
                        <div className="p-3 text-center border-r border-white/10">
                          <p className="text-xl font-serif text-amber-400">{clientDetail.orders?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Orders</p>
                        </div>
                        <div className="p-3 text-center border-r border-white/10">
                          <p className="text-xl font-serif text-purple-400">{clientDetail.alterations?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Alts</p>
                        </div>
                        <div className="p-3 text-center">
                          <p className="text-xl font-serif text-emerald-400">
                            ${(clientDetail.invoices || []).reduce((sum, inv) => sum + (inv.total || 0), 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                      </div>

                      {/* Tabs Content */}
                      <div className="p-6 max-h-[500px] overflow-y-auto">
                        {/* Notes Section */}
                        <div className="mb-6">
                          <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                            <MessageSquare size={18} className="text-primary" />
                            Notes
                          </h3>
                          <div className="flex gap-2 mb-3">
                            <Input
                              placeholder="Add a note about this client..."
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && handleAddNote()}
                              className="bg-background border-white/10"
                            />
                            <Button
                              onClick={handleAddNote}
                              disabled={addingNote || !newNote.trim()}
                              className="bg-primary text-primary-foreground"
                            >
                              {addingNote ? "..." : "Add"}
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(clientDetail.client?.notes || []).length === 0 ? (
                              <p className="text-sm text-muted-foreground py-2">No notes yet</p>
                            ) : (
                              clientDetail.client.notes.map((note, idx) => (
                                <div key={note.id || idx} className="bg-white/5 p-3 text-sm">
                                  <p>{note.note}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(note.created_at)}
                                  </p>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Invoices Section */}
                        <div className="mb-6">
                          <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                            <FileText size={18} className="text-blue-400" />
                            Invoices
                            {(clientDetail.invoices || []).length > 0 && (
                              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full ml-auto">
                                {clientDetail.invoices.length}
                              </span>
                            )}
                          </h3>
                          {(clientDetail.invoices || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No invoices</p>
                          ) : (
                            <div className="space-y-2">
                              {clientDetail.invoices.slice(0, 5).map((inv, idx) => (
                                <div key={inv.id || idx} className="bg-white/5 p-3 flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium">{inv.invoice_number}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatDate(inv.created_at)} • {(inv.items || []).length} items
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-bold text-white">${(inv.total || 0).toFixed(2)}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      inv.status === "paid" ? "bg-green-500/20 text-green-400" :
                                      inv.status === "overdue" ? "bg-red-500/20 text-red-400" :
                                      inv.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                                      "bg-slate-500/20 text-slate-400"
                                    }`}>
                                      {inv.status}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Orders Section */}
                        <div className="mb-6">
                          <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                            <Package size={18} className="text-amber-400" />
                            Orders
                            {(clientDetail.orders || []).length > 0 && (
                              <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full ml-auto">
                                {clientDetail.orders.length}
                              </span>
                            )}
                          </h3>
                          {(clientDetail.orders || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No orders</p>
                          ) : (
                            <div className="space-y-2">
                              {clientDetail.orders.slice(0, 5).map((order, idx) => (
                                <div key={order.id || idx} className="bg-white/5 p-3 flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium">{order.order_number}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {order.garment_type} • {formatDate(order.order_date || order.created_at)}
                                    </p>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    order.status === "delivered" ? "bg-green-500/20 text-green-400" :
                                    order.status === "ready" ? "bg-emerald-500/20 text-emerald-400" :
                                    order.status === "in_production" ? "bg-blue-500/20 text-blue-400" :
                                    "bg-amber-500/20 text-amber-400"
                                  }`}>
                                    {order.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Alterations Section */}
                        <div className="mb-6">
                          <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                            <Scissors size={18} className="text-purple-400" />
                            Alterations
                            {(clientDetail.alterations || []).length > 0 && (
                              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-auto">
                                {clientDetail.alterations.length}
                              </span>
                            )}
                          </h3>
                          {(clientDetail.alterations || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No alterations</p>
                          ) : (
                            <div className="space-y-2">
                              {clientDetail.alterations.slice(0, 5).map((alt, idx) => (
                                <div key={alt.id || idx} className="bg-white/5 p-3 flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium">{alt.tag}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Due: {formatDate(alt.due_date)} • ${(alt.total_fee || 0).toFixed(2)}
                                    </p>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    alt.status === "completed" ? "bg-green-500/20 text-green-400" :
                                    alt.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                                    "bg-amber-500/20 text-amber-400"
                                  }`}>
                                    {alt.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Recent Appointments */}
                        <div className="mb-6">
                          <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                            <Calendar size={18} className="text-primary" />
                            Appointments
                            {(clientDetail.appointments || []).length > 0 && (
                              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full ml-auto">
                                {clientDetail.appointments.length}
                              </span>
                            )}
                          </h3>
                          {(clientDetail.appointments || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No appointments</p>
                          ) : (
                            <div className="space-y-2">
                              {clientDetail.appointments.slice(0, 5).map((apt, idx) => (
                                <div key={apt.id || idx} className="bg-white/5 p-3 flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium">{apt.service_type || apt.service_name || "Appointment"}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {apt.appointment_date} at {apt.appointment_time}
                                    </p>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    apt.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                                    apt.status === "completed" ? "bg-green-500/20 text-green-400" :
                                    "bg-blue-500/20 text-blue-400"
                                  }`}>
                                    {apt.status || "pending"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Recent Emails */}
                        {(clientDetail.sent_emails || []).length > 0 && (
                          <div>
                            <h3 className="font-serif text-lg mb-3 flex items-center gap-2">
                              <Mail size={18} className="text-primary" />
                              Emails Sent
                            </h3>
                            <div className="space-y-2">
                              {clientDetail.sent_emails.slice(0, 3).map((email, idx) => (
                                <div key={email.id || idx} className="bg-white/5 p-3">
                                  <p className="text-sm font-medium">{email.subject}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Sent {formatDate(email.sent_at)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </motion.div>
              ) : (
                <div className="bg-card border border-white/10 p-12 text-center">
                  <Users size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Select a client to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Add/Edit Client Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111c2e] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {editMode ? "Edit Client" : "Add New Client"}
                </h3>
                <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Name *</label>
                  <Input
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    placeholder="John Doe"
                    className="bg-[#0a1628] border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Email *</label>
                  <Input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-[#0a1628] border-white/10 text-white"
                    disabled={editMode}
                  />
                  {editMode && <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>}
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Phone</label>
                  <Input
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="bg-[#0a1628] border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Address</label>
                  <Input
                    value={clientForm.address}
                    onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                    placeholder="123 Main St, City, State"
                    className="bg-[#0a1628] border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Status</label>
                  <Select 
                    value={clientForm.status} 
                    onValueChange={(v) => setClientForm({ ...clientForm, status: v })}
                  >
                    <SelectTrigger className="bg-[#0a1628] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!editMode && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Initial Note (optional)</label>
                    <Textarea
                      value={clientForm.notes}
                      onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                      placeholder="Add any notes about this client..."
                      rows={3}
                      className="bg-[#0a1628] border-white/10 text-white"
                    />
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-white/10 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveClient}
                  disabled={savingClient || !clientForm.name || !clientForm.email}
                  className="bg-primary hover:bg-primary/90"
                >
                  {savingClient ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      {editMode ? "Update Client" : "Add Client"}
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCRMPage;
